// security/geoFence.js
// BuzzInApp — Geofencing y validación de presencia física
//
// Jerarquía de precisión: GPS (3-10m) → WiFi (15-40m) → Cell (100-300m)
// Radio máximo permitido para canjear: 300 metros
//
// Expone:
//   getUserPosition()          → obtiene posición con fallback
//   validatePresence(pos, biz) → verifica si el usuario está dentro del radio
//   getAccuracyTier(accuracy)  → clasifica la fuente de ubicación

import { GEOFENCING, MOCK_LOCATION } from "../config/security";
import { detectMockLocation } from "./fraudDetector";

// ─── Obtener posición del usuario ─────────────────────────────────────────────

/**
 * Obtiene la posición actual del usuario.
 * Intenta primero GPS de alta precisión, luego baja precisión como fallback.
 *
 * @param {object} [options]
 * @param {number} [options.timeout=10000]          Timeout en ms
 * @param {boolean} [options.highAccuracy=true]     Intentar GPS primero
 * @returns {Promise<{
 *   position: GeolocationPosition | null,
 *   tier: "gps"|"wifi"|"cell"|"unavailable",
 *   error: string | null
 * }>}
 */
export const getUserPosition = (options = {}) => {
  const { timeout = 10000, highAccuracy = true } = options;

  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve({ position: null, tier: "unavailable", error: "geolocation_not_supported" });
      return;
    }

    // Intento 1: alta precisión (GPS)
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const tier = getAccuracyTier(pos.coords.accuracy);
        resolve({ position: pos, tier, error: null });
      },
      () => {
        // Intento 2: baja precisión (WiFi/Cell)
        if (!highAccuracy) {
          resolve({ position: null, tier: "unavailable", error: "location_denied" });
          return;
        }

        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const tier = getAccuracyTier(pos.coords.accuracy);
            resolve({ position: pos, tier, error: null });
          },
          (err) => {
            const errorMap = {
              1: "location_denied",       // PERMISSION_DENIED
              2: "location_unavailable",  // POSITION_UNAVAILABLE
              3: "location_timeout",      // TIMEOUT
            };
            resolve({
              position: null,
              tier:     "unavailable",
              error:    errorMap[err.code] ?? "location_unknown_error",
            });
          },
          { enableHighAccuracy: false, timeout, maximumAge: 30000 }
        );
      },
      { enableHighAccuracy: true, timeout, maximumAge: 5000 }
    );
  });
};

// ─── Clasificar precisión ─────────────────────────────────────────────────────

/**
 * Clasifica la fuente de ubicación según los metros de precisión.
 * @param {number} accuracy  Metros de precisión reportados por la API
 * @returns {"gps"|"wifi"|"cell"|"unknown"}
 */
export const getAccuracyTier = (accuracy) => {
  if (accuracy === null || accuracy === undefined) return "unknown";
  if (accuracy <= GEOFENCING.GPS_ACCURACY.GPS.max)  return "gps";
  if (accuracy <= GEOFENCING.GPS_ACCURACY.WIFI.max) return "wifi";
  if (accuracy <= GEOFENCING.GPS_ACCURACY.CELL.max) return "cell";
  return "unknown"; // precisión muy baja para confiar
};

// ─── Validación de presencia ──────────────────────────────────────────────────

/**
 * Valida si el usuario está físicamente dentro del radio del comercio.
 *
 * @param {GeolocationPosition} userPosition  Posición actual del usuario
 * @param {{ lat: number, lng: number }} business  Coordenadas del comercio (de Google Sheet)
 * @param {GeolocationPosition|null} [previousPosition]  Para detectar mock location
 * @returns {{
 *   present:        boolean,
 *   distanceMeters: number,
 *   tier:           string,
 *   gpsAbsent:      boolean,
 *   mockDetected:   boolean,
 *   mockFlags:      string[],
 *   reason:         string
 * }}
 */
export const validatePresence = (userPosition, business, previousPosition = null) => {
  // Sin GPS disponible: permitir canje pero registrar ausencia de GPS
  if (!userPosition) {
    return {
      present:        GEOFENCING.ALLOW_QR_WITHOUT_GPS,
      distanceMeters: null,
      tier:           "unavailable",
      gpsAbsent:      true,
      mockDetected:   false,
      mockFlags:      [],
      reason:         GEOFENCING.ALLOW_QR_WITHOUT_GPS ? "gps_absent_allowed" : "gps_required",
    };
  }

  const { latitude, longitude, accuracy } = userPosition.coords;
  const tier = getAccuracyTier(accuracy);

  // Calcular distancia al comercio
  const distanceMeters = haversineDistance(
    latitude,
    longitude,
    business.lat,
    business.lng
  );

  // Detectar mock location
  const mockResult = detectMockLocation(userPosition, previousPosition);

  // Presencia válida si está dentro del radio
  const withinRadius = distanceMeters <= GEOFENCING.MAX_DISTANCE_METERS;

  // Si hay mock location y el umbral lo requiere: bloquear o marcar para revisión
  const mockBlocks =
    mockResult.isMock &&
    mockResult.confidence === "high" &&
    MOCK_LOCATION.REQUIRE_EXTRA_VERIFICATION;

  let reason = "ok";
  if (!withinRadius)    reason = "too_far";
  else if (mockBlocks)  reason = "mock_location_detected";

  return {
    present:        withinRadius && !mockBlocks,
    distanceMeters: Math.round(distanceMeters),
    tier,
    gpsAbsent:      false,
    mockDetected:   mockResult.isMock,
    mockFlags:      mockResult.flags,
    reason,
  };
};

// ─── Distancia Haversine ──────────────────────────────────────────────────────

/**
 * Calcula la distancia entre dos puntos GPS en metros (fórmula Haversine).
 * @param {number} lat1
 * @param {number} lng1
 * @param {number} lat2
 * @param {number} lng2
 * @returns {number}  Distancia en metros
 */
export const haversineDistance = (lat1, lng1, lat2, lng2) => {
  const R  = 6371000; // radio de la Tierra en metros
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lng2 - lng1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) ** 2 +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;

  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// ─── Watch position (para ClaimScreen) ───────────────────────────────────────

/**
 * Suscripción continua a la posición del usuario.
 * Útil en ClaimScreen para actualizar la distancia en tiempo real mientras camina.
 *
 * @param {(result: object) => void} onUpdate  Callback con el mismo formato de validatePresence
 * @param {{ lat: number, lng: number }} business
 * @returns {() => void}  Función para cancelar el watch
 */
export const watchPosition = (onUpdate, business) => {
  if (!navigator.geolocation) {
    onUpdate({ present: false, tier: "unavailable", error: "geolocation_not_supported" });
    return () => {};
  }

  let previousPosition = null;

  const watchId = navigator.geolocation.watchPosition(
    (pos) => {
      const result = validatePresence(pos, business, previousPosition);
      previousPosition = pos;
      onUpdate(result);
    },
    (err) => {
      onUpdate({
        present:        false,
        distanceMeters: null,
        tier:           "unavailable",
        gpsAbsent:      true,
        mockDetected:   false,
        mockFlags:      [],
        reason:         `watch_error_${err.code}`,
      });
    },
    { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 }
  );

  return () => navigator.geolocation.clearWatch(watchId);
};
